import React from 'react';
import { Flex } from '@adobe/react-spectrum';
import ChevronIcon from 'app/assets/svgIcons/ChevronIcon';
import ChevronDropdownIcon from 'app/assets/figma/slideout/chevron-dropdown-old.svg';
import ChevronDownRiskIcon from 'app/assets/figma/slideout/chevron-dropdown-old.svg';
import CheckMarkCircleIcon from 'app/assets/svgIcons/CheckMarkCircleIcon';
import PrimaryContacts from '../PrimaryContacts/PrimaryContacts';
import GeneralInfoCommentIcon from '../CommentPanel/GeneralInfoCommentIcon/GeneralCommentIcon';
import { EditOnlyWrapper } from '../context/RBACContext';
import { CalendarRange } from '../../Fields/Calendar';
import {
  SlideoutMilestoneAccordionHeader,
  SlideoutMilestoneAddFooterSlot
} from './SlideoutMilestonesSection';
import ToolTipWrapper from 'app/components/ToolTipWrapper/ToolTipWrapper';
import { DelayedTooltip } from '../DelayedTooltip';
import { MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS } from '../configs/momentTypeConstants';
import { SlideoutPhasedGeneralInfo } from './SlideoutGeneralInfoSection';
import type { MomentData } from '../../../services/momentsApi';
import type { SlideOutPanelCard } from '../types/slideOutPanel.types';
import type { Contact as PCContact } from '../utils/primaryContactsManager';
import type { MilestoneSortPreferenceChoice } from '../hooks/useMilestoneSortPreference';
import styles from '../SlideOutPanel.module.scss';

export type NormalizedPhase = {
  id: string;
  phaseName?: string;
  description?: string;
  status?: string | null;
  statusUpdatedAt?: string;
  generalInfo?: Record<string, any>;
  [key: string]: any;
};

export type SlideoutCustomAccordionSectionProps = {
  normalizedPhases: NormalizedPhase[];
  customExpandedPhases: Set<string>;
  momentType: string;
  momentName: string;
  card: SlideOutPanelCard | null | undefined;
  apiData: MomentData | null;
  hasMomentLevelMilestones: boolean;
  rawMomentTypeForMilestones: string;
  areAllSectionsCollapsed: () => boolean;
  getGeneralInfo: (phaseId?: string) => Record<string, any>;
  flattenPrimaryContacts: (phaseId?: string) => any[];
  getMilestones: (phaseId?: string) => any;
  togglePhase: (phaseId: string) => void;
  canEditGeneralInfo: boolean;
  canEditMilestones: boolean;
  rtmButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  handleRtmToggle: (phaseId: string) => void;
  renderRtmDropdownPortal: (phaseId: string) => React.ReactNode;
  statusButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  handleStatusToggle: (phaseId: string) => void;
  formatStatusUpdatedDate: (date?: string) => string;
  renderStatusDropdownPortal: (phaseId: string) => React.ReactNode;
  openDateChipPhaseId: string | null;
  setOpenDateChipPhaseId: React.Dispatch<React.SetStateAction<string | null>>;
  getAccordionDateDisplay: (
    momentType: string,
    dates: any
  ) => { fieldName: string; displayText: string };
  getAccordionCalendarProps: (dates: any, fieldName: string) => any;
  getDateChipTooltip: (fieldName: string) => string;
  handleAccordionDateChange: (
    phaseId: string | null,
    phaseName: string | undefined,
    fieldName: string,
    calendarDate: import('@internationalized/date').CalendarDate | null,
    isQuarterSelection: boolean | undefined,
    selectedQuarter: { quarter: number; year: number } | undefined,
    isMonthSelection: boolean | undefined,
    selectedMonth: { month: string; year: number } | undefined,
    specialValue: 'TBD' | 'N/A' | null | undefined,
    currentDates: any,
    oldDisplayText: string
  ) => void;
  tierButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  handleTierToggle: (phaseId: string) => void;
  renderTierDropdownPortal: (phaseId: string) => React.ReactNode;
  revenueImpactButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  handleRevenueImpactToggle: (phaseId: string) => void;
  getRevenueImpactDisplayValue: (
    revenueImpact: any,
    isPhased?: boolean
  ) => string;
  renderRevenueImpactDropdownPortal: (phaseId: string) => React.ReactNode;
  riskButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  handleRiskLevelToggle: (phaseId: string) => void;
  getRiskLevelIcon: (riskLevel: any) => string;
  renderRiskLevelDropdownPortal: (phaseId: string) => React.ReactNode;
  expandedInfoSection: Set<string>;
  toggleInfoSection: (id: string) => void;
  handleGeneralInfoCommentIconClick: (phase: NormalizedPhase) => void;
  handleMilestonesCommentIconClick: (phase: NormalizedPhase) => void;
  eventsData: any[];
  isLoadingEvents: boolean;
  eventsError: string | null;
  parentMomentsData: any[];
  isLoadingParentMoments: boolean;
  parentMomentsError: string | null;
  resolveMomentType: () => string;
  handleSeamlessDataRefreshWithoutFeatures: (patchResponse?: unknown) => void;
  handleSeamlessDataRefresh: (patchResponse?: unknown) => void;
  onLoadAlertsAndNotifications: () => void;
  onPhasedGeneralInfoChange: (phaseId: string, updatedInfo: any) => void;
  refreshMomentNotifications: () => void;
  handleOpenPrimaryContactPopup: (phaseId: string) => void;
  onRoleContactsReplace: (
    phaseId: string,
    phaseName: string,
    role: string,
    roleContacts: PCContact[]
  ) => Promise<void>;
  onContactUpdate: (
    phaseId: string,
    phaseName: string,
    updatedContact: PCContact,
    index: number
  ) => Promise<void>;
  areMilestonesInherited: (phaseId: string) => boolean;
  shouldShowAddMilestoneButton: (phaseId?: string) => boolean;
  getReleasePhaseMilestones: (phaseId: string) => any[];
  renderInheritedMilestones: (phaseId: string) => React.ReactNode;
  renderReleasePhaseMilestones: (phaseId: string) => React.ReactNode;
  handleSortToggle: (phaseKey: string) => void;
  getSortDescriptiveText: (phaseKey: string) => string;
  sortDropdownOpen: Record<string, boolean>;
  getEffectiveMilestoneSort: (phaseKey: string) => string;
  handleSortSelect: (key: string, sort: MilestoneSortPreferenceChoice) => void;
  milestoneUpdateTrigger: number;
  milestoneMenuOpen: Record<string, boolean>;
  handleMilestoneMenuToggle: (menuKey: string) => void;
  handleDeleteMilestones: (
    phaseId?: string,
    phaseName?: string
  ) => Promise<void>;
  seamlessRefreshAndNotifications: (patchResponse?: unknown) => void;
  availableRtmOptions: string[];
  addPhaseButtonRef: React.RefObject<HTMLButtonElement | null>;
  addPhaseLoading: boolean;
  addPhaseDropdownOpen: boolean;
  setAddPhaseDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleAddPhase: (rtm: string) => void;
};

/**
 * Phased moment accordion layout (Part 3A-2).
 */
export function SlideoutCustomAccordionSection({
  normalizedPhases,
  customExpandedPhases,
  momentType,
  momentName,
  card,
  apiData,
  hasMomentLevelMilestones,
  rawMomentTypeForMilestones,
  areAllSectionsCollapsed,
  getGeneralInfo,
  flattenPrimaryContacts,
  getMilestones,
  togglePhase,
  canEditGeneralInfo,
  canEditMilestones,
  rtmButtonRefs,
  handleRtmToggle,
  renderRtmDropdownPortal,
  statusButtonRefs,
  handleStatusToggle,
  formatStatusUpdatedDate,
  renderStatusDropdownPortal,
  openDateChipPhaseId,
  setOpenDateChipPhaseId,
  getAccordionDateDisplay,
  getAccordionCalendarProps,
  getDateChipTooltip,
  handleAccordionDateChange,
  tierButtonRefs,
  handleTierToggle,
  renderTierDropdownPortal,
  revenueImpactButtonRefs,
  handleRevenueImpactToggle,
  getRevenueImpactDisplayValue,
  renderRevenueImpactDropdownPortal,
  riskButtonRefs,
  handleRiskLevelToggle,
  getRiskLevelIcon,
  renderRiskLevelDropdownPortal,
  expandedInfoSection,
  toggleInfoSection,
  handleGeneralInfoCommentIconClick,
  handleMilestonesCommentIconClick,
  eventsData,
  isLoadingEvents,
  eventsError,
  parentMomentsData,
  isLoadingParentMoments,
  parentMomentsError,
  resolveMomentType,
  handleSeamlessDataRefreshWithoutFeatures,
  handleSeamlessDataRefresh,
  onLoadAlertsAndNotifications,
  onPhasedGeneralInfoChange,
  refreshMomentNotifications,
  handleOpenPrimaryContactPopup,
  onRoleContactsReplace,
  onContactUpdate,
  areMilestonesInherited,
  shouldShowAddMilestoneButton,
  getReleasePhaseMilestones,
  renderInheritedMilestones,
  renderReleasePhaseMilestones,
  handleSortToggle,
  getSortDescriptiveText,
  sortDropdownOpen,
  getEffectiveMilestoneSort,
  handleSortSelect,
  milestoneUpdateTrigger,
  milestoneMenuOpen,
  handleMilestoneMenuToggle,
  handleDeleteMilestones,
  seamlessRefreshAndNotifications,
  availableRtmOptions,
  addPhaseButtonRef,
  addPhaseLoading,
  addPhaseDropdownOpen,
  setAddPhaseDropdownOpen,
  handleAddPhase
}: SlideoutCustomAccordionSectionProps) {
  return (
    <div
      data-testid="slideout-phase-accordion"
      className={`${styles.phaseAccordionContainer} ${
        areAllSectionsCollapsed() ? styles.allCollapsed : ''
      }`}
    >
      {normalizedPhases.map(phase => {
        const { id, phaseName, description } = phase;
        const generalInfo = getGeneralInfo(id);
        const contacts = flattenPrimaryContacts(id);
        const milestones = getMilestones(id);
        const expanded = phase.status !== 'N/A' && customExpandedPhases.has(id);

        // Create display name that includes description if available and phase name appears multiple times (inline implementation)
        const phaseCount = normalizedPhases.filter(
          p => p.phaseName === phaseName
        ).length;
        const displayName =
          phaseCount > 1 && description
            ? `${phaseName} - ${description}`
            : phaseName;
        return (
          <div
            key={id}
            data-testid={`slideout-phase-card-${id}`}
            data-phase-id={id}
            className={styles.phaseCard}
          >
            <div
              data-testid={`slideout-phase-header-${id}`}
              className={styles.phaseHeader}
            >
              {phase.status !== 'N/A' && (
                <div
                  data-testid={`slideout-phase-toggle-${id}`}
                  className={styles.phaseToggle}
                  onClick={() => togglePhase(id)}
                  style={{ cursor: 'pointer' }}
                >
                  <ChevronIcon isExpanded={expanded} />
                </div>
              )}
              <div
                data-testid={`slideout-phase-title-${id}`}
                className={styles.phaseTitle}
              >
                {/* For Price Changes and Offers: phaseName is the RTM — make it editable as a dropdown */}
                {momentType === MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS ? (
                  <div
                    className={styles.dropdownWrapper}
                    style={{ position: 'relative' }}
                    data-phase-id={id}
                  >
                    <button
                      data-testid={`slideout-rtm-dropdown-button-${id}`}
                      ref={el => {
                        if (el) {
                          rtmButtonRefs.current[id] = el;
                        }
                      }}
                      className={styles.rtmNameDropdown}
                      aria-label="RTM options"
                      disabled={!canEditGeneralInfo}
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (canEditGeneralInfo) {
                          handleRtmToggle(id);
                        }
                      }}
                    >
                      <span className={styles.rtmNameText}>{displayName}</span>
                      {canEditGeneralInfo && (
                        <span
                          className={styles.dropdownArrow}
                          aria-hidden="true"
                        >
                          <img src={ChevronDropdownIcon} alt="" />
                        </span>
                      )}
                    </button>
                    {renderRtmDropdownPortal(id)}
                  </div>
                ) : (
                  <span
                    data-testid={`slideout-phase-name-${id}`}
                    className={styles.phaseName}
                    onClick={() =>
                      phase.status === 'N/A' ? undefined : togglePhase(id)
                    }
                    style={{
                      cursor: phase.status === 'N/A' ? 'default' : 'pointer'
                    }}
                  >
                    {displayName}
                  </span>
                )}
                <span
                  data-testid={`slideout-phase-status-${id}`}
                  className={styles.phaseStatus}
                >
                  {/* Status Badge Display — only shown when reportingStatus has "Something - Level" format */}
                  {(() => {
                    const statusLabel =
                      generalInfo?.reportingStatus?.split(' - ')[1];
                    if (!statusLabel || phase.status === 'N/A') return null;
                    return (
                      <span
                        data-testid={`slideout-phase-status-badge-${id}`}
                        className={`${styles.statusBadge} ${
                          styles[statusLabel.toLowerCase()]
                        }`}
                      >
                        {statusLabel}
                      </span>
                    );
                  })()}
                  {/* Status dropdown - show for all phases except when explicitly hidden */}
                  {phase.status !== 'DRAFT' && (
                    <div
                      data-testid={`slideout-phase-status-dropdown-wrapper-${id}`}
                      className={styles.dropdownWrapper}
                      style={{ position: 'relative' }}
                      data-phase-id={id}
                    >
                      {/* The badge shows Completed only when BOTH the primary status and the
                          secondary date status (when this type has one) are done — either one
                          still Active means the phase as a whole reads Active. A type with no
                          secondary date (secondaryDateStatus is null/undefined) never blocks on
                          it, matching the backend's own null-tolerant semantics
                          (isReleasePhaseSecondaryDone). The "Completed on <date>" date itself is
                          still always the primary status's own statusUpdatedAt — unchanged. */}
                      {(() => {
                        const normalizedSecondary = (
                          phase.secondaryDateStatus || ''
                        ).toUpperCase();
                        const isSecondaryDone =
                          phase.secondaryDateStatus == null ||
                          normalizedSecondary === 'COMPLETE' ||
                          normalizedSecondary === 'COMPLETED';
                        const isFullyCompleted =
                          phase.status === 'COMPLETED' && isSecondaryDone;

                        return (
                          <button
                            data-testid={`slideout-phase-status-dropdown-button-${id}`}
                            ref={el => {
                              if (el) {
                                statusButtonRefs.current[id] = el;
                              }
                            }}
                            className={(() => {
                              // Apply different base styling based on actual status
                              if (isFullyCompleted) {
                                return `${styles.activeBadgeDropdown} ${styles.completedBadgeDropdown}`;
                              } else if (
                                phase.status === 'ACTIVE' ||
                                phase.status === 'COMPLETED'
                              ) {
                                return styles.activeBadgeDropdown; // Green styling — still Active overall
                              } else {
                                // For N/A, DRAFT, or other statuses - use neutral grey styling
                                return `${styles.activeBadgeDropdown} ${styles.completedBadgeDropdown}`;
                              }
                            })()}
                            aria-label={`${
                              phase.status || 'N/A'
                            } status options`}
                            style={{
                              cursor: canEditGeneralInfo ? 'pointer' : 'default'
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              e.preventDefault();

                              // Allow dropdown to open only if user has edit permissions
                              if (
                                canEditGeneralInfo &&
                                statusButtonRefs.current[id]
                              ) {
                                handleStatusToggle(id);
                              }
                            }}
                          >
                            <span>
                              {isFullyCompleted
                                ? `Completed on ${formatStatusUpdatedDate(
                                    phase.statusUpdatedAt
                                  )}`
                                : phase.status === 'ACTIVE' ||
                                  phase.status === 'COMPLETED'
                                ? 'Active'
                                : phase.status === 'N/A'
                                ? 'N/A'
                                : phase.status === null
                                ? 'NULL'
                                : phase.status || 'Active'}
                            </span>
                            <span className={styles.dropdownArrow}>
                              <img src={ChevronDropdownIcon} alt="dropdown" />
                            </span>
                          </button>
                        );
                      })()}
                      {renderStatusDropdownPortal(id)}
                    </div>
                  )}
                  {phase.status === 'DRAFT' && (
                    <span className={styles.draftBadge}>Draft</span>
                  )}
                </span>

                {/* Date Chip - hidden when N/A */}
                {phase.status !== 'N/A' &&
                  (() => {
                    const phaseDates = generalInfo?.dates;
                    const {
                      fieldName: dateFieldName,
                      displayText: dateDisplayText
                    } = getAccordionDateDisplay(momentType, phaseDates);
                    const calProps = getAccordionCalendarProps(
                      phaseDates,
                      dateFieldName
                    );
                    return (
                      <div
                        className={styles.dropdownWrapper}
                        style={{ position: 'relative' }}
                        data-phase-id={id}
                        data-testid={`slideout-phase-date-chip-wrapper-${id}`}
                      >
                        <DelayedTooltip
                          message={getDateChipTooltip(dateFieldName)}
                          cursor="pointer"
                          ariaLabel="Edit date"
                        >
                          <button
                            data-testid={`slideout-phase-date-chip-button-${id}`}
                            className={styles.tierBadgeDropdown}
                            aria-label="Edit date"
                            style={{
                              cursor: canEditGeneralInfo ? 'pointer' : 'default'
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (canEditGeneralInfo) {
                                setOpenDateChipPhaseId(
                                  openDateChipPhaseId === id ? null : id
                                );
                              }
                            }}
                          >
                            <span className={styles.tierText}>
                              {dateDisplayText}
                            </span>
                            <span className={styles.dropdownArrow}>
                              <img src={ChevronDownRiskIcon} alt="dropdown" />
                            </span>
                          </button>
                        </DelayedTooltip>
                        {openDateChipPhaseId === id && (
                          <div className={styles.calendarDropdown}>
                            <CalendarRange
                              mode="single"
                              skipTriggerButton={true}
                              hideYearList={true}
                              showTBDNA={true}
                              disablePastDate={false}
                              defaultSpecialValue={calProps.defaultSpecialValue}
                              defaultDate={calProps.defaultDate}
                              defaultMonth={calProps.defaultMonth}
                              defaultQuarter={calProps.defaultQuarter}
                              disableNA={calProps.disableNA}
                              disableTBD={calProps.disableTBD}
                              onCalendarDateChange={(
                                date,
                                isQuarter,
                                selQuarter,
                                isMonth,
                                selMonth,
                                special
                              ) => {
                                handleAccordionDateChange(
                                  id,
                                  phaseName,
                                  dateFieldName,
                                  date,
                                  isQuarter,
                                  selQuarter,
                                  isMonth,
                                  selMonth,
                                  special,
                                  phaseDates,
                                  dateDisplayText
                                );
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}

                {/* Tier Dropdown - hidden when N/A */}
                {phase.status !== 'N/A' && (
                  <div
                    className={styles.dropdownWrapper}
                    style={{ position: 'relative' }}
                    data-phase-id={id}
                  >
                    <button
                      ref={el => {
                        if (el) {
                          tierButtonRefs.current[id] = el;
                        }
                      }}
                      className={styles.tierBadgeDropdown}
                      aria-label="Tier options"
                      style={{
                        cursor: canEditGeneralInfo ? 'pointer' : 'default'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (canEditGeneralInfo && tierButtonRefs.current[id]) {
                          handleTierToggle(id);
                        }
                      }}
                    >
                      <span className={styles.tierText}>
                        {generalInfo?.tier || 'L1'}
                      </span>
                      <span className={styles.dropdownArrow}>
                        <img src={ChevronDownRiskIcon} alt="dropdown" />
                      </span>
                    </button>
                    {renderTierDropdownPortal(id)}
                  </div>
                )}

                {/* $ (Revenue Impact) Dropdown - only show for GA and Public Beta phases, disabled for read-only users */}
                {phase.status !== 'N/A' &&
                  (phase.phaseName?.toUpperCase() === 'GA' ||
                    phase.phaseName?.toUpperCase() === 'PUBLIC BETA') && (
                    <div
                      className={styles.dropdownWrapper}
                      style={{ position: 'relative' }}
                      data-phase-id={id}
                    >
                      <ToolTipWrapper message={`Impacts Revenue Targets`}>
                        <button
                          ref={el => {
                            if (el) {
                              revenueImpactButtonRefs.current[id] = el;
                            }
                          }}
                          className={styles.tierBadgeDropdown}
                          aria-label="$ Revenue Impact options"
                          style={{
                            cursor: canEditGeneralInfo ? 'pointer' : 'default'
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (
                              canEditGeneralInfo &&
                              revenueImpactButtonRefs.current[id]
                            ) {
                              handleRevenueImpactToggle(id);
                            }
                          }}
                        >
                          <span className={styles.tierText}>
                            $:{' '}
                            {getRevenueImpactDisplayValue(
                              generalInfo?.revenueImpact
                            )}
                          </span>
                          <span className={styles.dropdownArrow}>
                            <img src={ChevronDownRiskIcon} alt="dropdown" />
                          </span>
                        </button>
                      </ToolTipWrapper>
                      {renderRevenueImpactDropdownPortal(id)}
                    </div>
                  )}

                {/* Risk Level Dropdown - show checkmark when completed, otherwise show risk level */}
                {phase.status !== 'N/A' && (
                  <div
                    className={styles.dropdownWrapper}
                    style={{ position: 'relative' }}
                    data-phase-id={id}
                  >
                    {phase.status === 'COMPLETED' ? (
                      // Show checkmark icon for completed phases
                      <div className={styles.completedRiskIndicator}>
                        <CheckMarkCircleIcon width="18" height="18" />
                      </div>
                    ) : (
                      // Show risk level dropdown for non-completed phases
                      <>
                        <button
                          ref={el => {
                            if (el) {
                              riskButtonRefs.current[id] = el;
                            }
                          }}
                          className={styles.riskLevelBadgeDropdown}
                          aria-label="Risk level options"
                          style={{
                            cursor: canEditGeneralInfo ? 'pointer' : 'default'
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            // Ensure the ref is set before opening dropdown
                            if (
                              canEditGeneralInfo &&
                              riskButtonRefs.current[id]
                            ) {
                              handleRiskLevelToggle(id);
                            }
                          }}
                        >
                          <span className={styles.riskLevelIcon}>
                            <img
                              src={getRiskLevelIcon(generalInfo?.riskLevel)}
                              alt="risk level"
                            />
                          </span>
                          <span className={styles.dropdownArrow}>
                            <img src={ChevronDownRiskIcon} alt="dropdown" />
                          </span>
                        </button>
                        {renderRiskLevelDropdownPortal(id)}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            {expanded && (
              <div className={styles.phaseContent}>
                {/* General Info and Primary Contacts Headers Side by Side */}
                <div className={styles.phaseInfoContactsSection}>
                  <div className={styles.phaseGeneralInfo}>
                    <div
                      className={styles.infoSectionHeader}
                      onClick={() => toggleInfoSection(id)}
                    >
                      <div className={styles.infoHeaderContent}>
                        <div className={styles.phaseToggle}>
                          <ChevronIcon
                            isExpanded={expandedInfoSection.has(id)}
                          />
                        </div>
                        <Flex direction="row" alignItems="center">
                          <span className={styles.sectionTitleText}>
                            General Info
                          </span>
                          <GeneralInfoCommentIcon
                            phaseName={phase.phaseName}
                            categoryName="General Info"
                            onClick={() =>
                              handleGeneralInfoCommentIconClick(phase)
                            }
                          />
                        </Flex>
                      </div>
                    </div>
                    {expandedInfoSection.has(id) && (
                      <div className={styles.sectionContent}>
                        <SlideoutPhasedGeneralInfo
                          generalInfo={generalInfo}
                          phase={phase as any}
                          card={card as any}
                          rootMomentName={
                            apiData?.name ||
                            card?.name ||
                            card?.title ||
                            'Unknown'
                          }
                          rootMomentType={
                            apiData?.momentType ||
                            card?.momentType ||
                            resolveMomentType()
                          }
                          eventsData={eventsData}
                          isLoadingEvents={isLoadingEvents}
                          eventsError={eventsError}
                          parentMomentsData={parentMomentsData}
                          isLoadingParentMoments={isLoadingParentMoments}
                          parentMomentsError={parentMomentsError}
                          issueId={card?.issueId}
                          phaseName={phaseName}
                          onMilestoneRefresh={
                            handleSeamlessDataRefreshWithoutFeatures
                          }
                          onSeamlessRefresh={handleSeamlessDataRefresh}
                          onRefreshNotifications={() =>
                            onLoadAlertsAndNotifications()
                          }
                          onGeneralInfoChange={updatedInfo =>
                            onPhasedGeneralInfoChange(id, updatedInfo)
                          }
                          onNotificationRefresh={refreshMomentNotifications}
                        />
                      </div>
                    )}
                  </div>
                  <div className={styles.phasePrimaryContacts}>
                    <div className={styles.primaryContactsHeaderOnly}>
                      <div className={styles.infoHeaderContent}>
                        <span className={styles.sectionTitleText}>
                          Primary Contacts
                        </span>
                      </div>
                      <EditOnlyWrapper>
                        <button
                          className={styles.addButtonPrimary}
                          onClick={() => {
                            // Open the primary contact popup for this phase
                            handleOpenPrimaryContactPopup(id);
                          }}
                          aria-label="Add primary contact"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M8 3V13M3 8H13"
                              stroke="#909090"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      </EditOnlyWrapper>
                    </div>
                    {expandedInfoSection.has(id) && (
                      <div className={styles.sectionContent}>
                        <PrimaryContacts
                          contacts={contacts}
                          showHeader={false}
                          onExternalAddClick={() => {}}
                          onRoleContactsReplace={async (role, roleContacts) => {
                            await onRoleContactsReplace(
                              id,
                              phaseName!,
                              role,
                              roleContacts as PCContact[]
                            );
                          }}
                          onContactUpdate={async (updatedContact, index) => {
                            await onContactUpdate(
                              id,
                              phaseName!,
                              updatedContact as PCContact,
                              index
                            );
                          }}
                          onContactAdd={() => {
                            // No longer needed - handled at parent level
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                {/* Milestones Section - hidden for moment-level milestones (e.g. Price Changes and Offers) where milestones are shown as a top-level section */}
                {!hasMomentLevelMilestones && (
                  <div
                    className={`${styles.milestonesSection} ${
                      getReleasePhaseMilestones(id)?.length === 0 &&
                      !areMilestonesInherited(id)
                        ? styles.milestonesSectionEmpty
                        : ''
                    }`}
                  >
                    <SlideoutMilestoneAccordionHeader
                      titleSlot={
                        <Flex direction="row" alignItems="center">
                          <span className={styles.sectionTitleText}>
                            Milestones
                          </span>
                          <GeneralInfoCommentIcon
                            phaseName={phase.phaseName}
                            categoryName="Milestones"
                            onClick={() =>
                              handleMilestonesCommentIconClick(phase)
                            }
                          />
                        </Flex>
                      }
                      sortKey={`${phase.id}-${card?.issueId}`}
                      sortDropdownOpen={sortDropdownOpen}
                      getSortDescriptiveText={getSortDescriptiveText}
                      getEffectiveMilestoneSort={getEffectiveMilestoneSort}
                      handleSortToggle={handleSortToggle}
                      handleSortSelect={handleSortSelect}
                      milestones={getReleasePhaseMilestones(id)}
                      menuKey={`phased-menu-${id}-${card?.issueId}`}
                      menuReactKey={`milestone-menu-${id}-${milestoneUpdateTrigger}`}
                      momentName={`${phase.phaseName}`}
                      milestoneMenuOpen={milestoneMenuOpen}
                      handleMilestoneMenuToggle={handleMilestoneMenuToggle}
                      handleDeleteMilestones={handleDeleteMilestones}
                      phaseId={id}
                      phaseName={phase.phaseName}
                      controlsGap="12px"
                    />
                    <div className={styles.milestonesContent}>
                      {/* Inherited milestones FIRST with special styling */}
                      {areMilestonesInherited(id) &&
                        renderInheritedMilestones(id)}

                      {/* Conditional logic for normal milestones:
                        - If inheritMilestones = false: Show normal milestones when milestoneAdded = true
                        - If inheritMilestones = true: ALWAYS show normal milestones (both inherited and existing)
                        - If milestones exist in data: ALWAYS show them regardless of flags */}
                      {(!areMilestonesInherited(id) &&
                        shouldShowAddMilestoneButton(id) === false) ||
                      areMilestonesInherited(id) ||
                      getReleasePhaseMilestones(id).length > 0 ? (
                        <div>{renderReleasePhaseMilestones(id)}</div>
                      ) : null}
                    </div>

                    <SlideoutMilestoneAddFooterSlot
                      visible={
                        shouldShowAddMilestoneButton(id) &&
                        !!(milestones?.issueId || card?.issueId)
                      }
                      onDataRefresh={seamlessRefreshAndNotifications}
                      issueId={milestones?.issueId || card?.issueId!}
                      milestoneId={milestones?.milestoneId}
                      phaseId={id}
                      phaseName={phaseName}
                      momentName={momentName}
                      momentType={momentType}
                      disabled={!canEditMilestones}
                    />
                  </div>
                )}{' '}
                {/* end !hasMomentLevelMilestones */}
              </div>
            )}
          </div>
        );
      })}

      {/* Add Phase — PCO only (EOL has four fixed stages, no add) */}
      {hasMomentLevelMilestones &&
        rawMomentTypeForMilestones === MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS &&
        canEditGeneralInfo &&
        availableRtmOptions.length > 0 && (
          <div className={styles.addPhaseRow}>
            <div className={styles.addPhaseInner}>
              <button
                ref={addPhaseButtonRef as React.Ref<HTMLButtonElement>}
                className={styles.addPhaseButton}
                disabled={addPhaseLoading}
                onClick={() => setAddPhaseDropdownOpen(prev => !prev)}
                aria-label="Add Phase"
              >
                <span className={styles.addPhaseLabel}>
                  {addPhaseLoading ? 'Adding…' : 'Add Phase'}
                </span>
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={styles.addPhaseIcon}
                  aria-hidden="true"
                >
                  <path
                    d="M5 1v8M1 5h8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              {addPhaseDropdownOpen && (
                <div
                  className={styles.addPhaseDropdown}
                  data-add-phase-dropdown="true"
                >
                  {availableRtmOptions.map(rtm => (
                    <button
                      key={rtm}
                      className={styles.addPhaseDropdownItem}
                      onClick={() => handleAddPhase(rtm)}
                    >
                      {rtm}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
}
